// FE-SCRM-048 — TanStack Query hooks for the CSV import pipeline.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  commitImport,
  createImportSession,
  fetchImportDetail,
  fetchImportList,
  type CreateImportInput,
} from "./api";
import type {
  ImportListResponse,
  ImportSession,
} from "./types";


export const IMPORTS_QUERY_KEY = "sales-imports" as const;


export function useImportListQuery(): UseQueryResult<ImportListResponse> {
  return useQuery({
    queryKey: [IMPORTS_QUERY_KEY, "list"],
    queryFn: ({ signal }) => fetchImportList({ signal }),
  });
}


export function useImportDetailQuery(
  session_id: string | null,
  opts?: { refetchInterval?: number | false },
): UseQueryResult<ImportSession> {
  return useQuery({
    queryKey: [IMPORTS_QUERY_KEY, "detail", session_id],
    queryFn: ({ signal }) =>
      fetchImportDetail(session_id as string, { signal }),
    enabled: !!session_id,
    refetchInterval: opts?.refetchInterval,
  });
}


function _invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [IMPORTS_QUERY_KEY] });
}


export function useCreateImportMutation(): UseMutationResult<
  ImportSession, Error, CreateImportInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateImportInput) => createImportSession(input),
    onSuccess: () => _invalidateAll(qc),
  });
}


export function useCommitImportMutation(): UseMutationResult<
  ImportSession, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (session_id: string) => commitImport(session_id),
    onSuccess: () => _invalidateAll(qc),
  });
}
